import { suggestExitCapRate, benchmarkPricePerSqft } from './financialCalculations';

/**
 * A row from the comp pool. `compPoolService` joins `transactions` to
 * `master_properties`, so the cap rate arrives as `cap_rate` and the unit price
 * as `price_per_sf` - NOT as the `comp_cap_rate` / `comp_price_per_sqft` fields
 * that suggestExitCapRate and benchmarkPricePerSqft read. Passing a pool row
 * straight into either helper silently filters it out.
 */
export interface CompPoolRow {
  cap_rate?: number | null;
  price_per_sf?: number | null;
  transaction_date?: string | null;
  distance_miles?: number | null;
  property?: { address?: string | null; city?: string | null } | null;
}

export interface SupportingComp {
  address: string;
  city: string;
  capRate: number | null;
  pricePerSqft: number | null;
  date: string | null;
  distanceMiles: number | null;
}

/**
 * The curated comps a broker attached to a property. `/properties/:id/comps`
 * returns these, and they are what InvestmentAnalysis.tsx receives as its
 * `comps` prop.
 */
export interface LinkedComp {
  comp_address?: string;
  comp_city?: string;
  comp_cap_rate?: number | null;
  comp_price_per_sqft?: number | null;
  comp_sale_date?: string | null;
  distance_miles?: number | null;
}

/**
 * Bridge the analyzer's curated comps onto the evidence shape.
 *
 * Do NOT pass a `Comp[]` straight into buildExitCapEvidence. Every CompPoolRow
 * field is optional, so `Comp[]` is structurally assignable and tsc reports
 * nothing - but the helpers read `cap_rate` / `price_per_sf`, which a Comp does
 * not have, so the result is a null suggestion and an empty supporting list for
 * every deal. A compiled probe of that mistake produced zero type errors.
 *
 * Joseph's ruling 2026-07-31: ship this (curated comps) first. The eventual
 * target is linked comps preferred, falling back to the CoStar pool when a
 * property has none - gated on compPoolService excluding undated rows, since
 * undated rows are live asking prices, and asking prices bias exit caps DOWN,
 * which inflates exit value and flatters the IRR.
 */
export const linkedCompsToPoolRows = (comps: LinkedComp[]): CompPoolRow[] =>
  comps.map(c => ({
    cap_rate: c.comp_cap_rate ?? null,
    price_per_sf: c.comp_price_per_sqft ?? null,
    transaction_date: c.comp_sale_date ?? null,
    distance_miles: c.distance_miles ?? null,
    property: { address: c.comp_address ?? null, city: c.comp_city ?? null },
  }));

export const poolRowsToComps = (rows: CompPoolRow[]) =>
  rows.map(r => ({
    comp_cap_rate: r.cap_rate ?? null,
    comp_price_per_sqft: r.price_per_sf ?? null,
  }));

const toSupporting = (r: CompPoolRow): SupportingComp => ({
  address: r.property?.address || 'Unknown address',
  city: r.property?.city || '',
  capRate: r.cap_rate ?? null,
  pricePerSqft: r.price_per_sf ?? null,
  date: r.transaction_date ?? null,
  distanceMiles: r.distance_miles ?? null,
});

/**
 * What `GET /api/data-pool/exit-cap-comps/:id` returns. `supporting` is already
 * CompPoolRow-shaped on purpose, so the pool path and the linked-comp path both
 * run through buildExitCapEvidence rather than through two functions that can
 * drift apart - the dominant defect class in this build.
 */
export interface PoolExitCap {
  capRate: number | null;
  sampleSize: number;
  radiusMiles: number | null;
  windowMonths: number | null;
  supporting: CompPoolRow[];
  reason?: string;
}

export const buildExitCapEvidence = (rows: CompPoolRow[]) => {
  const supporting = rows.filter(r => r.cap_rate != null && r.cap_rate > 0).map(toSupporting);
  return {
    suggestedCap: suggestExitCapRate(poolRowsToComps(rows)),
    supporting,
  };
};

export const buildPricePsfEvidence = (rows: CompPoolRow[]) => {
  const supporting = rows.filter(r => r.price_per_sf != null && r.price_per_sf > 0).map(toSupporting);
  return {
    benchmark: benchmarkPricePerSqft(poolRowsToComps(rows)),
    supporting,
  };
};
